<script setup lang="ts">
import { computed, ref } from 'vue';
import { inviteFor, inviteUrl } from '@/api/share';
import { t } from '@/i18n';

/*
 * The invite: the code, big, and the ways to pass it on.
 *
 * On both game screens rather than the host's alone. The person who thinks of somebody else who
 * would want a square is usually a player — the host is busy running the pool, and may not even
 * be at their phone. It takes the game as props and touches no store, which is what
 * apps/web/docs/ARCHITECTURE.md asks of anything under components/.
 *
 * Each screen decides *when* to show it. Neither shows it once the game is locked: nobody can
 * join a locked game, so an invitation would be one to a closed door.
 */

const props = defineProps<{
  code: string;
  /** The game's name, for the share sheet's title. Null is fine — inviteFor words around it. */
  name?: string | null;
}>();

/** What just happened, for the live region. Empty means nothing worth saying. */
const said = ref('');

const origin = (): string => (typeof window === 'undefined' ? '' : window.location.origin);

const link = computed(() => inviteUrl(props.code, origin()));

/*
 * Read once, at setup. A share sheet cannot appear or disappear while a page is open, and a
 * button that arrives a tick late moves the ones beside it out from under somebody's thumb.
 *
 * Absent on desktop Firefox and on Chrome anywhere but Android and ChromeOS, which is why the
 * copy buttons are not a fallback but the main offering: a Share button that reports "not
 * supported" is worse than no Share button.
 */
const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/** `confirmation` is a message key, because "Link copied" is not one sentence in every language. */
async function copy(
  value: string,
  confirmation: 'share.copiedCode' | 'share.copiedLink',
): Promise<void> {
  // No `await` before writeText: Safari only allows a clipboard write inside the user gesture,
  // and anything awaited first puts it outside.
  const clipboard = navigator.clipboard;
  if (!clipboard) {
    said.value = t('share.copyFailed');
    return;
  }

  try {
    await clipboard.writeText(value);
    said.value = t(confirmation);
  } catch {
    said.value = t('share.copyFailed');
  }
}

async function share(): Promise<void> {
  said.value = '';

  try {
    await navigator.share(inviteFor({ code: props.code, name: props.name ?? null }, origin()));
  } catch (caught) {
    // Dismissing the sheet throws `AbortError`, and dismissing it is the single most common
    // outcome of opening one. Telling somebody their deliberate cancel failed is the bug this
    // branch exists to avoid.
    if (caught instanceof Error && caught.name === 'AbortError') return;

    // Anything else — a share refused outside a user gesture, a target that threw — and they
    // still wanted the link. The clipboard write is now outside the gesture and Safari may refuse
    // it in turn, which `copy` already has a sentence for.
    await copy(link.value, 'share.copiedLink');
  }
}
</script>

<template>
  <div class="rounded-lg border border-line bg-surface p-4 text-center">
    <p class="text-sm font-medium text-ink-muted">{{ t('share.heading') }}</p>
    <button
      type="button"
      :aria-label="t('share.codeAria', { code })"
      class="mt-2 font-mono text-4xl font-semibold tracking-[0.2em] hover:text-ink-muted sm:text-5xl"
      @click="copy(code, 'share.copiedCode')"
    >
      {{ code }}
    </button>
    <div class="mt-4 flex flex-wrap justify-center gap-3">
      <!-- First and filled where it exists: on a phone the share sheet is the whole job in one
           tap, and every group settles this in a chat app rather than by pasting a URL. -->
      <button
        v-if="canShare"
        type="button"
        class="rounded bg-accent px-4 py-2 text-sm font-medium text-on-accent hover:bg-accent-strong"
        @click="share"
      >
        {{ t('share.share') }}
      </button>
      <button
        type="button"
        class="rounded border border-accent px-4 py-2 text-sm font-medium hover:bg-fill"
        @click="copy(code, 'share.copiedCode')"
      >
        {{ t('share.copyCode') }}
      </button>
      <button
        type="button"
        class="rounded border border-accent px-4 py-2 text-sm font-medium hover:bg-fill"
        @click="copy(link, 'share.copiedLink')"
      >
        {{ t('share.copyLink') }}
      </button>
    </div>
    <p aria-live="polite" class="mt-3 min-h-5 text-sm text-ink-muted">{{ said }}</p>
    <p class="mt-2 text-sm text-ink-muted"><slot /></p>
  </div>
</template>
